import { z } from "zod";

// ─── Enums ───────────────────────────────────────────────────────────────────

export const JobPostingStatusSchema = z.enum(["draft", "active", "closed"]);
export type JobPostingStatus = z.infer<typeof JobPostingStatusSchema>;

export const CompensationTypeSchema = z.enum(["hourly", "salary", "range", "hourly_plus_tips"]);
export type CompensationType = z.infer<typeof CompensationTypeSchema>;

export const QuestionTypeSchema = z.enum([
  "text",
  "multiple_choice",
  "yes_no",
  "video",
]);
export type QuestionType = z.infer<typeof QuestionTypeSchema>;

// ─── Sub-schemas ─────────────────────────────────────────────────────────────

export const JobCompensationSchema = z.object({
  min: z.number().optional(),
  max: z.number().optional(),
  type: CompensationTypeSchema,
});
export type JobCompensation = z.infer<typeof JobCompensationSchema>;

export const JobQuestionSchema = z.object({
  type: QuestionTypeSchema,
  prompt: z.string(),
  options: z.array(z.string()).optional(),
  maxDuration: z.number().optional(),
});
export type JobQuestion = z.infer<typeof JobQuestionSchema>;

// ─── Core Entity ─────────────────────────────────────────────────────────────

export const JobPostingSchema = z.object({
  jobId: z.uuid(),
  title: z.string(),
  status: JobPostingStatusSchema,
  companyName: z.string(),
  companyLogo: z.string().optional(),
  companyId: z.uuid(),
  roleName: z.string().optional(),
  roleId: z.string().optional(),
  locationName: z.string().optional(),
  locationAddress: z.string().optional(),
  locationId: z.string().optional(),
  compensation: JobCompensationSchema.optional(),
  description: z.string().optional(),
  requirements: z.string().optional(),
  schedule: z.record(z.string(), z.unknown()).optional(),
  scheduleConfiguration: z.object({
    daysSlider: z.object({
      defaultValue: z.number(),
      max: z.number(),
    }),
    hoursSlider: z.object({
      defaultValue: z.number(),
      max: z.number(),
    }),
  }).optional(),
  questions: z.array(JobQuestionSchema).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string().optional(),
});
export type JobPosting = z.infer<typeof JobPostingSchema>;

// ─── Response Schemas ─────────────────────────────────────────────────────────

export const JobListResponseSchema = z.object({
  jobs: z.array(JobPostingSchema),
  count: z.number(),
});
export type JobListResponse = z.infer<typeof JobListResponseSchema>;

// ─── Request Schemas ──────────────────────────────────────────────────────────

export const CreateJobPostingRequestSchema = z.object({
  title: z.string().min(1),
  status: JobPostingStatusSchema.optional(),
  roleName: z.string().optional(),
  roleId: z.string().optional(),
  locationName: z.string().optional(),
  locationAddress: z.string().optional(),
  locationId: z.string().optional(),
  companyName: z.string().optional(),
  compensation: JobCompensationSchema.optional(),
  description: z.string().optional(),
  requirements: z.string().optional(),
  schedule: z.record(z.string(), z.unknown()).optional(),
  scheduleConfiguration: z.object({
    daysSlider: z.object({
      defaultValue: z.number(),
      max: z.number(),
    }),
    hoursSlider: z.object({
      defaultValue: z.number(),
      max: z.number(),
    }),
  }).optional(),
  questions: z.array(JobQuestionSchema).optional(),
});
export type CreateJobPostingRequest = z.infer<
  typeof CreateJobPostingRequestSchema
>;

export const UpdateJobPostingRequestSchema =
  CreateJobPostingRequestSchema.partial();
export type UpdateJobPostingRequest = z.infer<
  typeof UpdateJobPostingRequestSchema
>;
